<script setup>
import { computed, ref } from 'vue'
import { useVModel } from '@vueuse/core'
import { DxForm } from 'devextreme-vue/form'

import { getI18n } from '@/plugins/i18n'

import * as dataGrid from '@/composables/data-grid'
import { useNotify } from '@/composables/notify'

import { useRegistryStore } from '@/stores/registry.store'

import GenericPopup from '@/components/shared/GenericPopup.vue'

const props = defineProps({
  visible: {
    type: Boolean,
    default: false
  },
})

const emit = defineEmits(['update:visible', 'created'])

const i18n = getI18n()
const registryStore = useRegistryStore()
const { notifySuccess } = useNotify()

const formRef = ref(null)
const formData = ref({
  code: '',
  name: '',
  branch_ref: registryStore.getLocalizedBranches().find(branch => branch.code === '0')?.id ?? null,
})
const isSaving = ref(false)

const popupVisible = useVModel(props, 'visible', emit)

const popupOptions = computed(() => ({
  item: {},
  title: i18n.t('common.supplierBrand'),
  width: '42rem',
  maxWidth: 'calc(100vw - 2rem)',
  saveButton: { disabled: isSaving.value },
  cancelButton: { disabled: isSaving.value },
}))

const formItems = computed(() => [
  {
    itemType: 'group',
    caption: i18n.t('common.mainInfo'),
    colCount: 2,
    colCountByScreen: { lg: 2, md: 2, sm: 1, xs: 1 },
    items: [
      {
        dataField: 'code',
        label: { text: i18n.t('common.code') },
        editorOptions: dataGrid.useCodeFieldEditorOptions({ minLength: 2, maxLength: 3 }),
        validationRules: [{ type: 'required' }],
      },
      {
        dataField: 'branch_ref',
        label: { text: i18n.t('common.branch') },
        editorType: 'dxSelectBox',
        editorOptions: {
          ...dataGrid.useLookup({ store: registryStore.getLocalizedBranches() }),
          searchEnabled: true,
        },
        validationRules: [{ type: 'required' }],
      },
      {
        dataField: 'name',
        label: { text: i18n.t('common.description') },
        colSpan: 2,
        editorOptions: { maxLength: 50 },
        validationRules: [{ type: 'required' }],
      },
    ],
  },
])

const close = () => {
  if (!isSaving.value) popupVisible.value = false
}

const save = async() => {
  if (isSaving.value) return

  formData.value.code = formData.value.code?.trim()
  formData.value.name = formData.value.name?.trim()

  if (!formRef.value?.instance?.validate()?.isValid) return

  const supplierBrandData = { ...formData.value }
  isSaving.value = true
  try {
    const response = await registryStore.api.createSupplierBrandAsync(supplierBrandData)
    const supplierBrands = await registryStore.fetchSupplierBrandsAsync(true)
    const createdSupplierBrand = supplierBrands.find(brand => brand.code === supplierBrandData.code) ?? {
      ...response.data,
      id: -response.data.id,
      is_portal: false,
    }

    emit('created', createdSupplierBrand)
    notifySuccess(i18n.t('common.success_create'))
    popupVisible.value = false
  } finally {
    isSaving.value = false
  }
}
</script>

<template>
  <generic-popup
    v-model:visible="popupVisible"
    :options="popupOptions"
    @save="save"
    @cancel="close"
  >
    <div class="q-px-md q-pt-sm q-pb-md">
      <dx-form
        ref="formRef"
        :form-data="formData"
        :items="formItems"
        :col-count="1"
        :show-colon-after-label="false"
        label-location="top"
      />
    </div>
  </generic-popup>
</template>
